import { memo, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getComPostsAction,
  clearCommunityPostsAction,
  clearCreatePostFail,
} from "../../redux/actions/postActions";
import PostForm from "../form/PostForm";
import Post from "../post/Post";
import PostSkeleton from "../loader/PostSkeleton";
import { useToast } from "../../contexts/ToastContext";
import LoadMoreButton from "../shared/LoadMoreButton";

const MemoizedPost = memo(Post);

const PostSkeletonLoader = () => (
  <div className="w-full space-y-3 flex flex-col">
    {[...Array(3)].map((_, index) => (
      <PostSkeleton key={index} />
    ))}
  </div>
);

const MainSection = () => {
  const dispatch = useDispatch();
  const { error: showError } = useToast();

  const communityData = useSelector((state) => state.community?.communityData);
  const communityPosts = useSelector((state) => state.posts?.communityPosts);
  const joinedCommunities = useSelector((state) => state.community?.joinedCommunities);

  const totalCommunityPosts = useSelector(
    (state) => state.posts?.totalCommunityPosts
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const LIMIT = 10;

  // Check if user is a member of this community
  const isMember = joinedCommunities?.some(
    (community) => community._id === communityData?._id || community.name === communityData?.name
  );

  const postError = useSelector((state) => state.posts?.postError);

  // Handle toast notifications
  useEffect(() => {
    if (postError) {
      showError(postError);
      dispatch(clearCreatePostFail());
    }
  }, [postError, dispatch, showError]);

  useEffect(() => {
    const fetchInitialPosts = async () => {
      if (communityData?._id) {
        dispatch(getComPostsAction(communityData._id, LIMIT, 0)).finally(() => {
          setIsLoading(false);
        });
      }
    };

    fetchInitialPosts();

    return () => {
      dispatch(clearCommunityPostsAction());
    };
  }, [dispatch, communityData]);

  const handleLoadMore = () => {
    if (
      !isLoadMoreLoading &&
      communityPosts.length > 0 &&
      communityPosts.length < totalCommunityPosts
    ) {
      setIsLoadMoreLoading(true);
      dispatch(
        getComPostsAction(communityData._id, LIMIT, communityPosts.length)
      ).finally(() => {
        setIsLoadMoreLoading(false);
      });
    }
  };

  const memoizedCommunityPosts = useMemo(() => {
    return communityPosts?.map((post) => (
      <MemoizedPost key={post._id} post={post} />
    )) || [];
  }, [communityPosts]);

  return (
    <div className="flex flex-col gap-4 w-full max-w-3xl mx-auto">
      {isMember && (
        <PostForm
          communityId={communityData?._id}
          communityName={communityData?.name}
        />
      )}
      
      {isLoading || !communityData ? (
        <PostSkeletonLoader />
      ) : (
        <>
          <div className="w-full space-y-3 flex flex-col">
            {memoizedCommunityPosts}
            {communityPosts.length === 0 && (
              <div className="w-full bg-white border rounded-lg p-8 text-center">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No posts yet</h3>
                <p className="text-gray-500 font-roboto-flex">Be the first to share something in this community!</p>
              </div>
            )}
          </div>
          {communityPosts.length >= LIMIT && communityPosts.length < totalCommunityPosts && (
            <LoadMoreButton
              onClick={handleLoadMore}
              isLoading={isLoadMoreLoading}
              text="Load More Posts"
            />
          )}
        </>
      )}
    </div>
  );
};

export default MainSection;
